// src/pages/AssignStudents.jsx
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getCourse, getAllStudents, enrollStudent, getStudentEnrollment, updateFeeStatus, unenrollStudent } from "../firebase/database";

export default function AssignStudents() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [feePaid, setFeePaid] = useState(false);
  const [enrolledList, setEnrolledList] = useState({}); // { studentId: enrollmentData }

  useEffect(() => {
    loadData();
  }, [courseId]);

  const loadData = async () => {
    try {
      const [courseData, allStudents] = await Promise.all([
        getCourse(courseId),
        getAllStudents()
      ]);

      // Filter only students (not staff)
      const studentList = allStudents.filter(s => s.role === "student" || !s.id.startsWith("STAFF-"));

      // Get enrollment status for each student
      const enrolled = {};
      await Promise.all(
        studentList.map(async (student) => {
          const enrollment = await getStudentEnrollment(student.id, courseId);
          if (enrollment) {
            enrolled[student.id] = enrollment;
          }
        })
      );

      setCourse(courseData);
      setStudents(studentList);
      setEnrolledList(enrolled);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStudent = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.id));
    }
  };

  // Get students who are already enrolled (not in selected list)
  const alreadyEnrolled = students.filter(s => enrolledList[s.id]);

  const handleEnroll = async () => {
    if (selectedStudents.length === 0) {
      alert("Please select at least one student");
      return;
    }

    setSaving(true);
    try {
      for (const studentId of selectedStudents) {
        // Skip if already enrolled
        if (enrolledList[studentId]) {
          continue;
        }
        await enrollStudent(studentId, courseId, course.name, feePaid);
      }
      alert("Students enrolled successfully!");
      setSelectedStudents([]);
      loadData(); // Reload to show updated enrollments
    } catch (err) {
      console.error("Error enrolling students:", err);
      alert("Failed to enroll students");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFeePaid = async (studentId, currentFeePaid) => {
    try {
      await updateFeeStatus(studentId, courseId, !currentFeePaid);
      setEnrolledList(prev => ({
        ...prev,
        [studentId]: { ...prev[studentId], feePaid: !currentFeePaid }
      }));
    } catch (err) {
      console.error("Error updating fee status:", err);
      alert("Failed to update fee status");
    }
  };

  const handleUnenroll = async (studentId, studentName) => {
    if (!window.confirm(`Are you sure you want to unenroll "${studentName}" from this course? All progress will be lost.`)) {
      return;
    }
    try {
      await unenrollStudent(studentId, courseId);
      setEnrolledList(prev => {
        const newList = { ...prev };
        delete newList[studentId];
        return newList;
      });
      setStudents(students.filter(s => s.id !== studentId));
    } catch (err) {
      console.error("Error unenrolling student:", err);
      alert("Failed to unenroll student");
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
        <div style={{ color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>Loading...</div>
      </div>
    );
  }

  if (!course) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>
        Course not found
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <Link 
            to="/admin/courses" 
            style={{ color: "#6B7280", textDecoration: "none", display: "flex", alignItems: "center" }}
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1F2937", margin: 0, fontFamily: "DM Sans, sans-serif" }}>
            Assign Students
          </h1>
        </div>
        <p style={{ color: "#6B7280", margin: "4px 0 0 36px", fontFamily: "DM Sans, sans-serif" }}>
          {course.name} — Enroll students to this course
        </p>
      </div>

      {/* Course Info Card */}
      <div style={{ 
        background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)", 
        borderRadius: 12, 
        padding: 24, 
        marginBottom: 24,
        color: "white"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 8px 0", fontFamily: "DM Sans, sans-serif" }}>
              {course.name}
            </h2>
            <div style={{ display: "flex", gap: 24, fontSize: 14, opacity: 0.9, fontFamily: "DM Sans, sans-serif" }}>
              <span>{course.totalLessons || 0} Lessons</span>
              <span>{course.totalDurationHours || 0} Hours</span>
              <span>₹{course.fees || 0}</span>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 32, fontWeight: 700, fontFamily: "DM Sans, sans-serif" }}>
              {alreadyEnrolled.length}
            </div>
            <div style={{ fontSize: 14, opacity: 0.8, fontFamily: "DM Sans, sans-serif" }}>Enrolled</div>
          </div>
        </div>
      </div>

      {/* Enroll Form */}
      <div style={{ 
        background: "white", 
        borderRadius: 12, 
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)", 
        padding: 24, 
        marginBottom: 24 
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: "#1F2937", margin: "0 0 16px 0", fontFamily: "DM Sans, sans-serif" }}>
          Enroll New Students
        </h3>

        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}>
            <input
              type="checkbox"
              checked={feePaid}
              onChange={(e) => setFeePaid(e.target.checked)}
              style={{ width: 18, height: 18, cursor: "pointer" }}
            />
            <span style={{ fontSize: 15, color: "#374151" }}>Mark fee as paid for selected students</span>
          </label>
        </div>

        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button
            onClick={handleSelectAll}
            style={{
              padding: "10px 16px",
              background: "white",
              color: "#374151",
              border: "1px solid #D1D5DB",
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              fontFamily: "DM Sans, sans-serif",
              cursor: "pointer",
            }}
          >
            {selectedStudents.length === students.length ? "Deselect All" : "Select All"}
          </button>
          <button
            onClick={handleEnroll}
            disabled={saving || selectedStudents.length === 0}
            style={{
              padding: "10px 24px",
              background: saving || selectedStudents.length === 0 ? "#9CA3AF" : "#3B82F6",
              color: "white",
              border: "none",
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "DM Sans, sans-serif",
              cursor: saving || selectedStudents.length === 0 ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Enrolling..." : `Enroll Selected (${selectedStudents.length})`}
          </button>
        </div>
      </div>

      {/* Student List */}
      <div style={{ 
        background: "white", 
        borderRadius: 12, 
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)", 
        overflow: "hidden" 
      }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #E5E7EB" }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: "#1F2937", margin: 0, fontFamily: "DM Sans, sans-serif" }}>
            All Students ({students.length})
          </h3>
        </div>

        {students.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>
            No students found. Please enroll students first.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {students.map((student, index) => {
              const isEnrolled = !!enrolledList[student.id];
              const isSelected = selectedStudents.includes(student.id);
              
              return (
                <div 
                  key={student.id} 
                  style={{ 
                    padding: "16px 24px", 
                    borderBottom: index < students.length - 1 ? "1px solid #E5E7EB" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: isEnrolled ? "#F0FDF4" : "transparent",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1 }}>
                    {isEnrolled ? (
                      <span style={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: "50%", 
                        background: "#16A34A", 
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 14,
                        fontWeight: 600,
                      }}>
                        ✓
                      </span>
                    ) : (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleStudent(student.id)}
                        style={{ width: 20, height: 20, cursor: "pointer" }}
                      />
                    )}
                    <div>
                      <div style={{ fontWeight: 600, color: "#1F2937", fontFamily: "DM Sans, sans-serif" }}>
                        {student.name}
                      </div>
                      <div style={{ fontSize: 13, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>
                        Roll: {student.roll || student.id}
                      </div>
                    </div>
                  </div>
                  
                  {isEnrolled && (
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{
                        padding: "4px 12px",
                        borderRadius: 20,
                        fontSize: 13,
                        fontWeight: 500,
                        fontFamily: "DM Sans, sans-serif",
                        background: enrolledList[student.id].feePaid ? "#D1FAE5" : "#FEE2E2",
                        color: enrolledList[student.id].feePaid ? "#047857" : "#DC2626",
                      }}>
                        {enrolledList[student.id].feePaid ? "Fee Paid" : "Fee Pending"}
                      </span>
                      <button
                        onClick={() => handleToggleFeePaid(student.id, enrolledList[student.id].feePaid)}
                        style={{
                          padding: "6px 12px",
                          fontSize: 12,
                          background: "white",
                          color: "#374151",
                          border: "1px solid #D1D5DB",
                          borderRadius: 6,
                          cursor: "pointer",
                          fontFamily: "DM Sans, sans-serif",
                          fontWeight: 500,
                        }}
                      >
                        Toggle Fee
                      </button>
                      <button
                        onClick={() => handleUnenroll(student.id, student.name)}
                        style={{
                          padding: "6px 12px",
                          fontSize: 12,
                          background: "#FEE2E2",
                          color: "#DC2626",
                          border: "none",
                          borderRadius: 6,
                          cursor: "pointer",
                          fontFamily: "DM Sans, sans-serif",
                          fontWeight: 500,
                        }}
                      >
                        Unenroll
                      </button>
                    </div>
                  )}
                  
                  {!isEnrolled && (
                    <span style={{ color: "#9CA3AF", fontSize: 13, fontFamily: "DM Sans, sans-serif" }}>
                      Not enrolled
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}